import React from 'react';
import { getSource } from './iconSources';

/**
 * Rendu d'une icône à partir d'un objet { name, source }.
 * Supporte plusieurs bibliothèques via le registre `iconSources.js` :
 *   - tabler (défaut) : rendu synchrone (import statique)
 *   - lucide / fa     : rendu lazy via next/dynamic (chunk chargé à la demande)
 *
 * @param {{ name: string, source?: string }} icon
 * @param {number} [size=20]
 * @param {string|object} [colorOrOptions]
 *   - string  → couleur explicite (ex: "var(--primary)", "#fff", "red")
 *   - objet   → { color, stroke, className, style, ... } passé au composant
 *   - omis    → "currentColor" : l'icône hérite de la couleur CSS du parent
 *
 * Exemples :
 *   {renderIcon(icon)}                              // suit le `color:` du parent
 *   {renderIcon(icon, 18)}                          // taille 18, suit le parent
 *   {renderIcon(icon, 18, "var(--primary)")}        // couleur primary
 *   {renderIcon(icon, 18, { color: "var(--secondary)", stroke: 2 })}
 */
export default function renderIcon(icon, size = 20, colorOrOptions) {
    if (!icon || !icon.name) return null;

    // Honore icon.source (corrige l'ancien comportement qui forçait Tabler) ;
    // fallback sur tabler pour les valeurs legacy sans `source`.
    const source = getSource(icon.source);
    const Component = source.getComponent(icon.name);
    if (!Component) return null;

    // Normalise le 3e argument : string => { color }, objet => spread tel quel.
    const opts = typeof colorOrOptions === 'string'
        ? { color: colorOrOptions }
        : (colorOrOptions || {});

    // Par défaut : currentColor → l'icône suit la couleur du parent CSS.
    const color = opts.color ?? 'currentColor';

    // Tabler = composant synchrone ; lucide/fa = composant next/dynamic (ssr:false)
    // qui gère lui-même son chargement → rendu direct dans les deux cas.
    return <Component size={size} color={color} {...opts} />;
}
